/**
 * Member list of a SPLIT family group on the payers step: suggested parents,
 * one row per member with its percentage, add / remove, distribute equally.
 */
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { AlertCircle, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { PercentageSlider } from "@/components/form/fields/PercentageSlider";
import type { ContractFamilyGroupDraft } from "@/types/billing";
import type { Contact } from "@/types/contact";

interface FamilyMembersCardProps {
  members: ContractFamilyGroupDraft["members"];
  contacts?: Contact[];
  /** Parent contacts of the child, offered while the list is empty. */
  suggestedMembers: Array<{ contactId: string }>;
  /** Contacts that are not members yet. */
  availableContacts: Array<Contact & { id: string }>;
  onAddMember: (contactId: string) => void;
  onRemoveMember: (index: number) => void;
  onPercentageChange: (index: number, percentage: number) => void;
  onDistributeEqually: () => void;
}

export function FamilyMembersCard({
  members,
  contacts,
  suggestedMembers,
  availableContacts,
  onAddMember,
  onRemoveMember,
  onPercentageChange,
  onDistributeEqually,
}: FamilyMembersCardProps) {
  const { i18n } = useLingui();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          <Trans>Family Members</Trans>
        </CardTitle>
        <CardDescription>
          <Trans>Add contacts to the family billing group</Trans>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Auto-detected parents suggestion */}
        {suggestedMembers.length > 0 &&
          members.length === 0 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <Trans>
                  We found {suggestedMembers.length} parent(s)
                  associated with this child. Click "Add Suggested
                  Parents" to include them automatically.
                </Trans>
                <div className="mt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      suggestedMembers.forEach((member) => {
                        onAddMember(member.contactId);
                      });
                    }}
                  >
                    <Trans>Add Suggested Parents</Trans>
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

        {/* Member List */}
        <div className="space-y-3">
          {members.map((member, index) => {
            const contact = contacts?.find(
              (c) => c.id === member.contactId
            );
            return (
              <div
                key={index}
                className="flex flex-wrap items-center gap-3 p-3 border rounded-lg sm:flex-nowrap"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {contact
                        ? `${contact.firstname} ${contact.lastname}`
                        : t(i18n)`Unknown Contact`}
                    </span>
                  </div>
                  {contact?.emailMain && (
                    <span className="text-sm text-muted-foreground">
                      {contact.emailMain}
                    </span>
                  )}
                </div>

                <div className="w-48">
                  <PercentageSlider
                    field={{
                      name: `member-${index}`,
                      // `FieldConfig` requires these; the slider renders its
                      // own label from the markup above and ignores both.
                      label: "",
                      type: "percentage",
                    }}
                    value={member.paymentPercentage || 0}
                    onChange={(value) =>
                      onPercentageChange(index, value)
                    }
                    id={`member-percentage-${index}`}
                    min={0}
                    max={100}
                    step={1}
                    showDecimals={false}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemoveMember(index)}
                    aria-label={i18n._({
                      id: "contracts.wizard.payers.removeFamilyMember",
                      message: "Remove family member",
                    })}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add Member */}
        {availableContacts.length > 0 && (
          <div>
            <Label className="mb-2 block">
              <Trans>Add Family Member</Trans>
            </Label>
            <div className="flex gap-2 flex-wrap">
              {availableContacts.map((contact) => (
                <Button
                  key={contact.id}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onAddMember(contact.id)}
                >
                  {contact.firstname} {contact.lastname}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Auto-distribute for SPLIT billing */}
        {members.length > 1 && (
          <Button
            type="button"
            variant="outline"
            onClick={onDistributeEqually}
          >
            <Trans>Distribute Equally</Trans>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
